/**
 * FCM web registration against the existing VitePWA service worker.
 * Locked to firebase/messaging getToken + getMessaging (firebase 12.10.0).
 * Does not register a second service worker.
 */

import { getMessaging, getToken, isSupported, deleteToken } from 'firebase/messaging';
import app from '../../config/firebaseApp.js';

export function readVapidKey(env = import.meta.env) {
  const raw = env?.VITE_FIREBASE_VAPID_KEY;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : '';
}

export async function waitForActiveServiceWorker(navigatorLike = navigator, timeoutMs = 15_000) {
  if (!navigatorLike?.serviceWorker) {
    throw new Error('service_worker_unavailable');
  }
  const ready = await Promise.race([
    navigatorLike.serviceWorker.ready,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('service_worker_timeout')), timeoutMs);
    }),
  ]);
  return ready;
}

/**
 * Obtain an FCM registration token for the current installation.
 * Caller must already have Notification.permission === 'granted'.
 */
export async function obtainProspectPushFcmToken({
  vapidKey = readVapidKey(),
  messagingFactory = () => getMessaging(app),
  getTokenImpl = getToken,
  isSupportedImpl = isSupported,
  waitForSw = waitForActiveServiceWorker,
} = {}) {
  if (!vapidKey) throw new Error('vapid_missing');
  const supported = await isSupportedImpl();
  if (!supported) throw new Error('messaging_unsupported');
  const registration = await waitForSw();
  const messaging = messagingFactory();
  const token = await getTokenImpl(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });
  if (typeof token !== 'string' || !token.trim()) {
    throw new Error('token_unavailable');
  }
  return { token: token.trim(), registration };
}

export async function deleteProspectPushFcmToken({
  messagingFactory = () => getMessaging(app),
  deleteTokenImpl = deleteToken,
  isSupportedImpl = isSupported,
} = {}) {
  try {
    const supported = await isSupportedImpl();
    if (!supported) return { ok: false, reason: 'unsupported' };
    const messaging = messagingFactory();
    await deleteTokenImpl(messaging);
    return { ok: true };
  } catch {
    return { ok: false, reason: 'delete_failed' };
  }
}
